// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/engine.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/shortcuts/src/engine.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo field-check-app
//     node fleet/fleet.ts check --repo field-check-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * The dispatcher: single global keydown listener, scope stack, chord buffer.
 *
 * The engine has no Solid dependency — it consumes a Registry instance and a
 * mutable WhenContext map (Solid binds in solid.tsx).
 */

import { eventMatchesSignature, type KeySignature, parseKeySequence, signatureKey } from "./keys";
import type { Registry } from "./registry";
import {
  type KeyBinding,
  type PlatformAdapter,
  SCOPE_PRIORITY,
  type ScopeId,
  type ShortcutDispatchResult,
  type WhenContext,
} from "./types";
import { compileWhen } from "./when";

const DEFAULT_CHORD_TIMEOUT_MS = 1000;
const CHORD_RESET_PROGRESS_KEY = "__chord_in_progress__";

interface ScopeFrame {
  id: ScopeId;
  token: number;
}

interface CompiledBinding {
  binding: KeyBinding;
  sequence: KeySignature[];
  whenPredicate: (ctx: WhenContext) => boolean;
  commandWhenPredicate: (ctx: WhenContext) => boolean;
}

export interface Engine {
  start: () => void;
  stop: () => void;
  setContext: (key: string, value: boolean) => void;
  pushScope: (id: ScopeId) => () => void;
  context: () => WhenContext;
  dispatch: (commandId: string, event?: KeyboardEvent) => ShortcutDispatchResult;
  /** For tests. */
  simulate: (event: KeyboardEvent) => ShortcutDispatchResult;
}

interface EngineOptions {
  registry: Registry;
  platform: PlatformAdapter;
  contextStore: WhenContext;
  /** Default chord timeout in ms. */
  chordTimeoutMs?: number;
  /** Document/Element to attach listener to. */
  target?: EventTarget;
}

export function createEngine(opts: EngineOptions): Engine {
  const {
    registry,
    platform,
    contextStore,
    chordTimeoutMs = DEFAULT_CHORD_TIMEOUT_MS,
    target = typeof document === "undefined" ? globalThis : document,
  } = opts;

  let started = false;
  const scopeStack: ScopeFrame[] = [{ id: "global", token: 0 }];
  let scopeTokenSeq = 1;

  let chordBuffer: KeySignature[] = [];
  let chordTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  const resetChord = () => {
    chordBuffer = [];
    if (chordTimer !== undefined) {
      clearTimeout(chordTimer);
      chordTimer = undefined;
    }
    contextStore[CHORD_RESET_PROGRESS_KEY] = false;
  };

  const activeScopes = (): ScopeId[] => {
    const ids = new Set<ScopeId>();
    for (const frame of scopeStack) {
      ids.add(frame.id);
    }
    return [...ids].toSorted((a, b) => SCOPE_PRIORITY[b] - SCOPE_PRIORITY[a]);
  };

  const safeParseSequence = (keys: string): KeySignature[] | null => {
    try {
      return parseKeySequence(keys);
    } catch {
      return null;
    }
  };

  const compileBindings = (): CompiledBinding[] => {
    const result: CompiledBinding[] = [];
    for (const binding of registry.bindings()) {
      if (binding.platforms && !binding.platforms.includes(platform.id)) {
        continue;
      }
      if (platform.reservedBindings.has(binding.keys)) {
        continue;
      }
      const command = registry.getCommand(binding.command);
      if (!command) {
        continue;
      }
      if (command.disabledOn?.includes(platform.id)) {
        continue;
      }
      const sequence = safeParseSequence(binding.keys);
      if (sequence === null || sequence.length === 0) {
        continue;
      }
      result.push({
        binding,
        sequence,
        whenPredicate: compileWhen(binding.when),
        commandWhenPredicate: compileWhen(command.when),
      });
    }
    return result;
  };

  const chordPrefixMatches = (candidate: CompiledBinding, prefix: number): boolean => {
    for (let i = 0; i < prefix; i += 1) {
      const a = chordBuffer[i];
      const b = candidate.sequence[i];
      if (!a || !b || signatureKey(a) !== signatureKey(b)) {
        return false;
      }
    }
    return true;
  };

  const collectCandidates = (
    event: KeyboardEvent,
    compiled: CompiledBinding[],
    pendingPrefix: number,
  ): CompiledBinding[] => {
    const out: CompiledBinding[] = [];
    for (const c of compiled) {
      if (c.sequence.length <= pendingPrefix) {
        continue;
      }
      if (!chordPrefixMatches(c, pendingPrefix)) {
        continue;
      }
      const step = c.sequence[pendingPrefix];
      if (!step) {
        continue;
      }
      if (!eventMatchesSignature(event, step, platform.isMac)) {
        continue;
      }
      if (!c.commandWhenPredicate(contextStore)) {
        continue;
      }
      if (!c.whenPredicate(contextStore)) {
        continue;
      }
      out.push(c);
    }
    return out;
  };

  const advanceChord = (step: KeySignature): void => {
    chordBuffer.push(step);
    if (chordTimer !== undefined) {
      clearTimeout(chordTimer);
    }
    chordTimer = setTimeout(resetChord, chordTimeoutMs);
    contextStore[CHORD_RESET_PROGRESS_KEY] = true;
  };

  const compareWinners = (a: CompiledBinding, b: CompiledBinding): number => {
    const scopeA = SCOPE_PRIORITY[a.binding.scope ?? "global"];
    const scopeB = SCOPE_PRIORITY[b.binding.scope ?? "global"];
    if (scopeA !== scopeB) {
      return scopeB - scopeA;
    }
    const prioA = a.binding.priority ?? 0;
    const prioB = b.binding.priority ?? 0;
    if (prioA !== prioB) {
      return prioB - prioA;
    }
    const sourceA = a.binding.source === "user" ? 1 : 0;
    const sourceB = b.binding.source === "user" ? 1 : 0;
    return sourceB - sourceA;
  };

  const pickWinner = (
    completes: CompiledBinding[],
    activeScopeSet: ReadonlySet<ScopeId>,
  ): CompiledBinding | undefined => {
    const sorted = completes.toSorted(compareWinners);
    return sorted.find((c) => {
      const scope = c.binding.scope ?? "global";
      return scope === "global" || activeScopeSet.has(scope);
    });
  };

  const handleEvent = (event: KeyboardEvent): ShortcutDispatchResult => {
    if (event.defaultPrevented) {
      return { matched: false, reason: "no-match" };
    }

    const compiled = compileBindings();
    const scopes = activeScopes();
    const pendingPrefix = chordBuffer.length;

    const candidates = collectCandidates(event, compiled, pendingPrefix);
    if (candidates.length === 0) {
      resetChord();
      return { matched: false, reason: "no-match" };
    }

    const completes: CompiledBinding[] = [];
    let chordProgress = false;
    for (const c of candidates) {
      if (c.sequence.length === pendingPrefix + 1) {
        completes.push(c);
      } else {
        chordProgress = true;
      }
    }

    if (completes.length === 0 && chordProgress) {
      const step = candidates[0]?.sequence[pendingPrefix];
      if (step) {
        advanceChord(step);
      }
      return { matched: false, reason: "chord-progress" };
    }

    const winner = pickWinner(completes, new Set(scopes));
    if (!winner) {
      resetChord();
      return { matched: false, reason: "no-match" };
    }
    resetChord();

    const command = registry.getCommand(winner.binding.command);
    if (!command) {
      return { matched: false, reason: "no-match" };
    }
    const handler = registry.getHandler(command.id);
    if (!handler) {
      return { matched: false, reason: "no-match" };
    }

    if (command.preventDefault !== false) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      const result = handler({
        event,
        command: command.id,
        platform: platform.id,
      });
      // Handlers may return `false` to opt out; we then leave the event untouched.
      // Best-effort: there is no way to "undo" preventDefault, so we warn in dev.
      if (result === false && command.preventDefault !== false && typeof console !== "undefined") {
        console.warn(
          "[shortcuts] handler for",
          command.id,
          "returned false after preventDefault — this is a no-op",
        );
      }
      // If the handler returned a Promise, surface rejections in dev.
      if (result instanceof Promise) {
        void (async () => {
          try {
            await result;
          } catch (err) {
            if (typeof console !== "undefined") {
              console.error("[shortcuts] async handler rejected for", command.id, err);
            }
          }
        })();
      }
    } catch (err) {
      if (typeof console !== "undefined") {
        console.error("[shortcuts] handler threw for", command.id, err);
      }
    }

    return { matched: true, command: command.id };
  };

  const listener = (event: Event) => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    handleEvent(event);
  };

  return {
    start() {
      if (started) {
        return;
      }
      started = true;
      target.addEventListener("keydown", listener, { capture: true });
    },
    stop() {
      if (!started) {
        return;
      }
      started = false;
      target.removeEventListener("keydown", listener, { capture: true });
      resetChord();
    },
    setContext(key, value) {
      contextStore[key] = value;
    },
    pushScope(id) {
      const frame: ScopeFrame = { id, token: scopeTokenSeq };
      scopeTokenSeq += 1;
      scopeStack.push(frame);
      return () => {
        const idx = scopeStack.findIndex((f) => f.token === frame.token);
        if (idx !== -1) {
          scopeStack.splice(idx, 1);
        }
      };
    },
    context: () => contextStore,
    dispatch(commandId, event) {
      const handler = registry.getHandler(commandId);
      if (!handler) {
        return { matched: false, reason: "no-match" };
      }
      const synthetic =
        event ??
        new KeyboardEvent("keydown", {
          key: "Unidentified",
          bubbles: false,
          cancelable: true,
        });

      try {
        const result = handler({ event: synthetic, command: commandId, platform: platform.id });
        if (result instanceof Promise) {
          void (async () => {
            try {
              await result;
            } catch (err) {
              if (typeof console !== "undefined") {
                console.error("[shortcuts] dispatch async handler rejected", commandId, err);
              }
            }
          })();
        }
      } catch (err) {
        if (typeof console !== "undefined") {
          console.error("[shortcuts] dispatch handler threw for", commandId, err);
        }
      }
      return { matched: true, command: commandId };
    },
    simulate: handleEvent,
  };
}
